import {
  publicHolidayLabel,
  publicHolidayLabelEn,
} from "@/config/japanPublicHolidays";

const START_MINUTES = 9 * 60;
const BREAK_START = "12:00";
const BREAK_END = "13:00";
const LUNCH_BREAK_THRESHOLD_HOURS = 5;
const PTO_LEAVE_LABEL = "有給休暇";
const PTO_LEAVE_LABEL_EN = "Paid leave";

const JA_WEEKDAYS = [
  "日曜日",
  "月曜日",
  "火曜日",
  "水曜日",
  "木曜日",
  "金曜日",
  "土曜日",
];
const EN_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseRowHours(row) {
  /** Prefer timer-backed fields; skip zero placeholders that hide real hours. */
  for (const key of ["hours_with_timer", "working_hours", "hours"]) {
    const raw = row[key];
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Clock time on a calendar day (wraps at midnight). */
export function minutesToClockTimeLabel(totalMinutes) {
  if (!Number.isFinite(totalMinutes)) return "";
  const mins = Math.max(0, Math.round(totalMinutes));
  const hh = Math.floor(mins / 60) % 24;
  const mm = mins % 60;
  return `${hh}:${pad2(mm)}`;
}

/** Elapsed duration (no 24h wrap — used for daily/monthly hour totals). */
export function minutesToDurationLabel(totalMinutes) {
  if (!Number.isFinite(totalMinutes)) return "";
  const mins = Math.max(0, Math.round(totalMinutes));
  const hh = Math.floor(mins / 60);
  const mm = mins % 60;
  return `${hh}:${pad2(mm)}`;
}

/** @param {number} decimalHours */
export function decimalHoursToDurationLabel(decimalHours) {
  const h = Number(decimalHours);
  if (!Number.isFinite(h) || h <= 0) return "";
  return minutesToDurationLabel(Math.round(h * 60));
}

/** @param {number} decimalHours */
export function decimalHoursToHhMm(decimalHours) {
  return decimalHoursToDurationLabel(decimalHours);
}

function monthDateKeys(monthKey) {
  const [y, mo] = String(monthKey).split("-").map(Number);
  if (!y || !mo) return [];
  const last = new Date(y, mo, 0).getDate();
  const keys = [];
  for (let d = 1; d <= last; d++) {
    keys.push(`${y}-${pad2(mo)}-${pad2(d)}`);
  }
  return keys;
}

function weekdayLabels(dateKey) {
  const [y, mo, d] = String(dateKey).split("-").map(Number);
  const dt = new Date(y, mo - 1, d);
  const idx = dt.getDay();
  return {
    weekday: JA_WEEKDAYS[idx] || "",
    weekdayEn: EN_WEEKDAYS[idx] || "",
  };
}

function isWeekend(dateKey) {
  const [y, mo, d] = String(dateKey).split("-").map(Number);
  const day = new Date(y, mo - 1, d).getDay();
  return day === 0 || day === 6;
}

function isPtoProject(projectName) {
  return (
    String(projectName || "")
      .trim()
      .toUpperCase() === "PTO"
  );
}

/**
 * Derive clock times from reported work hours (9:00 start, 12–13 break when ≥5h).
 * @param {number} workHoursDecimal
 */
export function computeAttendanceClockTimes(workHoursDecimal) {
  const hours = Number(workHoursDecimal);
  if (!Number.isFinite(hours) || hours <= 0) {
    return {
      startTime: "",
      finishTime: "",
      breakStart: "",
      breakEnd: "",
      totalTime: hours === 0 ? "0:00" : "",
    };
  }
  const useBreak = hours >= LUNCH_BREAK_THRESHOLD_HOURS;
  const workMinutes = Math.round(hours * 60);
  const finishMinutes = START_MINUTES + workMinutes + (useBreak ? 60 : 0);
  return {
    startTime: "9:00",
    finishTime: minutesToClockTimeLabel(finishMinutes),
    breakStart: useBreak ? BREAK_START : "",
    breakEnd: useBreak ? BREAK_END : "",
    totalTime: decimalHoursToDurationLabel(hours),
  };
}

/**
 * Aggregate one user's timesheet lines by calendar day.
 * @param {object[]} timesheetRows normalized rows for one user
 * @param {Record<number, string>} projectNameById
 */
export function aggregateUserTimesheetByDate(timesheetRows, projectNameById) {
  /** @type {Record<string, { workHours: number, ptoHours: number, hasPtoEntry: boolean }>} */
  const byDate = {};
  (timesheetRows || []).forEach((row) => {
    const dateKey = String(row.log_date || "").slice(0, 10);
    if (!dateKey) return;
    const hours = parseRowHours(row);
    const projectName =
      projectNameById[row.project_id] || row.project_name || row.project || "";
    const pto = isPtoProject(projectName);
    if (!byDate[dateKey]) {
      byDate[dateKey] = {workHours: 0, ptoHours: 0, hasPtoEntry: false};
    }
    if (pto) {
      byDate[dateKey].ptoHours += hours;
      byDate[dateKey].hasPtoEntry = true;
    } else {
      byDate[dateKey].workHours += hours;
    }
  });
  return byDate;
}

const DEFAULT_PTO_DAY_HOURS = 8;

function resolvePtoDayHours(dayPtoHours, ptoDefaultDayHours) {
  const logged = Number(dayPtoHours);
  if (Number.isFinite(logged) && logged > 0) return logged;
  const fallback = Number(ptoDefaultDayHours);
  return Number.isFinite(fallback) && fallback > 0
    ? fallback
    : DEFAULT_PTO_DAY_HOURS;
}

/**
 * Distribute target monthly hours across weekdays (Chris / 月給 pattern).
 * @param {Record<string, { workHours: number, ptoHours: number, hasPtoEntry: boolean }>} byDate
 */
export function computeMonthlyDistributedHours(monthKey, byDate, targetHours) {
  const target = Number(targetHours);
  if (!Number.isFinite(target) || target <= 0) return {};

  const dateKeys = monthDateKeys(monthKey);
  const ptoDates = new Set();
  dateKeys.forEach((dk) => {
    const d = byDate[dk];
    if (d && d.workHours <= 0 && d.hasPtoEntry) ptoDates.add(dk);
  });

  const workingDays = dateKeys.filter((dk) => {
    if (isWeekend(dk)) return false;
    if (publicHolidayLabel(dk)) return false;
    if (ptoDates.has(dk)) return false;
    return true;
  });

  /** @type {Record<string, number>} */
  const hours = {};
  workingDays.forEach((dk) => {
    hours[dk] = 8;
  });

  let excess = workingDays.length * 8 - target;
  let i = workingDays.length - 1;
  while (excess > 0 && i >= 0) {
    const dk = workingDays[i];
    const cur = hours[dk];
    if (cur >= 8) {
      hours[dk] = 5;
      excess -= 3;
    } else if (cur >= 5) {
      hours[dk] = 4;
      excess -= 1;
    } else if (cur > 0) {
      excess -= cur;
      hours[dk] = 0;
    } else {
      i -= 1;
      continue;
    }
    if (excess > 0) i -= 1;
  }

  let sum = workingDays.reduce((acc, dk) => acc + (hours[dk] || 0), 0);
  let deficit = target - sum;
  i = workingDays.length - 1;
  while (deficit > 0 && i >= 0) {
    const dk = workingDays[i];
    const cur = hours[dk] || 0;
    const add = Math.min(deficit, 8 - cur);
    if (add > 0) {
      hours[dk] = cur + add;
      deficit -= add;
    }
    i -= 1;
  }

  return hours;
}

/**
 * Build 出勤簿 daily rows + month summary for one employee.
 * @param {string} monthKey YYYY-MM
 * @param {object[]} userTimesheetRows
 * @param {Record<number, string>} projectNameById
 * @param {{ monthlyTreatment?: boolean, monthlyTargetHours?: number, ptoDefaultDayHours?: number }} [payrollOptions]
 */
export function buildPayrollAttendanceBook(
  monthKey,
  userTimesheetRows,
  projectNameById,
  payrollOptions = {}
) {
  const ptoDefaultDayHours =
    Number(payrollOptions.ptoDefaultDayHours) > 0
      ? Number(payrollOptions.ptoDefaultDayHours)
      : DEFAULT_PTO_DAY_HOURS;
  const monthRows = (userTimesheetRows || []).filter((row) => {
    const dk = String(row.log_date || "").slice(0, 7);
    return dk === monthKey;
  });
  const byDate = aggregateUserTimesheetByDate(monthRows, projectNameById);
  const monthlyTreatment = !!payrollOptions.monthlyTreatment;
  const distributedHours = monthlyTreatment
    ? computeMonthlyDistributedHours(
        monthKey,
        byDate,
        payrollOptions.monthlyTargetHours
      )
    : null;
  const dateKeys = monthDateKeys(monthKey);

  let ptoDays = 0;
  let ptoHours = 0;
  let holidayDaysInMonth = 0;

  const dailyRows = dateKeys.map((dateKey) => {
    const holiday = publicHolidayLabel(dateKey);
    const holidayEn = publicHolidayLabelEn(dateKey);
    if (holiday) holidayDaysInMonth += 1;
    const {weekday, weekdayEn} = weekdayLabels(dateKey);

    const day = byDate[dateKey];
    const dayPtoHours = day ? day.ptoHours : 0;
    const ptoOnly = !!(day && day.workHours <= 0 && day.hasPtoEntry);
    const workHours = monthlyTreatment
      ? ptoOnly
        ? 0
        : distributedHours[dateKey] || 0
      : day
      ? day.workHours
      : 0;

    if (ptoOnly) {
      ptoDays += 1;
      ptoHours += resolvePtoDayHours(dayPtoHours, ptoDefaultDayHours);
    }

    const clock = computeAttendanceClockTimes(workHours);
    let leaveLabel = "";
    let leaveLabelEn = "";
    let leaveHoursLabel = "";

    if (ptoOnly && workHours <= 0) {
      leaveLabel = PTO_LEAVE_LABEL;
      leaveLabelEn = PTO_LEAVE_LABEL_EN;
      leaveHoursLabel = decimalHoursToDurationLabel(
        resolvePtoDayHours(dayPtoHours, ptoDefaultDayHours)
      );
    } else if (holiday && workHours <= 0 && !ptoOnly) {
      leaveLabel = holiday;
      leaveLabelEn = holidayEn;
    }

    const isNonWorking =
      workHours <= 0 &&
      !clock.startTime &&
      (isWeekend(dateKey) || holiday || ptoOnly || dayPtoHours > 0);

    return {
      dateKey,
      dateLabel: dateKey.replace(/-/g, "/"),
      weekday,
      weekdayEn,
      startTime: clock.startTime,
      finishTime: clock.finishTime,
      breakStart: clock.breakStart,
      breakEnd: clock.breakEnd,
      totalTime: workHours > 0 ? clock.totalTime : ptoOnly ? "0:00" : "",
      leaveLabel,
      leaveLabelEn,
      leaveHoursLabel,
      workHoursDecimal: workHours,
      isWeekend: isWeekend(dateKey),
      isHoliday: !!holiday,
      isNonWorking,
    };
  });

  const totalWorkHours = dailyRows.reduce(
    (sum, row) => sum + (Number(row.workHoursDecimal) || 0),
    0
  );
  const daysWorked = dailyRows.filter(
    (row) => (Number(row.workHoursDecimal) || 0) > 0
  ).length;

  const summary = {
    totalWorkTime: decimalHoursToHhMm(totalWorkHours),
    totalWorkHoursDecimal: totalWorkHours,
    daysWorked,
    closedDays: 0,
    ptoDays,
    ptoTime: decimalHoursToHhMm(ptoHours),
    ptoHoursDecimal: ptoHours,
    holidayDaysInMonth,
  };

  return {
    dailyRows,
    summary,
    monthlyTreatment,
  };
}

export const PAYROLL_ATTENDANCE_COLUMNS = [
  {key: "dateLabel", label: "日付", labelEn: "Date"},
  {key: "weekday", label: "曜日", labelEn: "Day"},
  {key: "startTime", label: "勤務開始時刻", labelEn: "Start"},
  {key: "finishTime", label: "勤務終了時刻", labelEn: "Finish"},
  {key: "breakStart", label: "休憩開始時間", labelEn: "Break start"},
  {key: "breakEnd", label: "休憩終了時間", labelEn: "Break end"},
  {key: "totalTime", label: "合計勤務時間", labelEn: "Total"},
  {key: "leaveLabel", label: "休暇", labelEn: "Leave"},
  {key: "leaveHoursLabel", label: "休暇時間", labelEn: "Leave hours"},
];

export const PAYROLL_ATTENDANCE_SUMMARY_ROWS = [
  {key: "totalWorkTime", label: "勤務時間合計", labelEn: "Total work time"},
  {key: "daysWorked", label: "出勤日数", labelEn: "Days worked"},
  {key: "closedDays", label: "休業日数", labelEn: "Closed days"},
  {key: "ptoDays", label: "有給取得（日数換算）", labelEn: "Paid leave (days)"},
  {key: "ptoTime", label: "有給時間", labelEn: "Paid leave hours"},
  {
    key: "holidayDaysInMonth",
    label: "祝日（当月）",
    labelEn: "Public holidays (month)",
  },
];
